import { ChatHeader } from '../chat/ChatHeader'
import { MessageList } from '../chat/MessageList'
import { TextComposer } from '../chat/TextComposer'
import { useChatSession } from '../chat/useChatSession'
import { VoiceControls } from '../chat/VoiceControls'

// header 下沿的渐进模糊带:气泡滚近 header 时文字被逐层糊化(越靠上越糊),柔化硬边界。
// 单层 backdrop-blur 半径恒定,所以叠多层「越往上 blur 越大、覆盖越窄」+ 纯线性 mask。
//
// 为何固定高度也不露边界(踩过的坑):
//  - 当初那道明显分界其实是**另一层固定的 opacity 渐隐遮罩**(原在 MessageList,已删),不是模糊本身;
//  - backdrop-filter 会在盒子下沿硬裁剪——所以盒子(BAND_H+8)要比"模糊归零点"(BAND_H)更高,
//    把硬裁剪推进 0 模糊区、再落进气泡间空隙(mb-3.5),看不见。
// fade 用占 BAND_H 的比例:最弱层 1.0 在 BAND_H 处归零,越强的层占比越小、集中在顶部。
const BAND_H = 120
const BLUR_LAYERS = [
    { blur: 0.35, fade: 1.0 },
    { blur: 0.7, fade: 0.875 },
    { blur: 1.15, fade: 0.75 },
    { blur: 1.75, fade: 0.625 },
    { blur: 2.5, fade: 0.5 },
    { blur: 3.5, fade: 0.375 },
    { blur: 4.8, fade: 0.27 },
    { blur: 6.4, fade: 0.17 }
]
function HeaderBlurMask() {
    return (
        <div className="pointer-events-none absolute inset-x-0 top-14 z-30" style={{ height: BAND_H + 8 }}>
            {BLUR_LAYERS.map((l) => {
                const mask = `linear-gradient(to bottom, #000 0px, transparent ${(l.fade * BAND_H).toFixed(1)}px)`
                return (
                    <div
                        key={l.blur}
                        className="absolute inset-0"
                        style={{
                            backdropFilter: `blur(${l.blur}px)`,
                            WebkitBackdropFilter: `blur(${l.blur}px)`,
                            maskImage: mask,
                            WebkitMaskImage: mask
                        }}
                    />
                )
            })}
        </div>
    )
}

interface ChatProps {
    theme: 'light' | 'dark'
    onChangeTheme: (next: 'light' | 'dark') => void
    persona: string
    onChangePersona: (next: string) => void
    fontSize: number
    onChangeFontSize: (px: number) => void
    blurAi: boolean
    onToggleBlurAi: () => void
    onBack: () => void
    onLogout: () => void
}

export function Chat({
    theme,
    onChangeTheme,
    persona,
    onChangePersona,
    fontSize,
    onChangeFontSize,
    blurAi,
    onToggleBlurAi,
    onBack,
    onLogout
}: ChatProps) {
    const s = useChatSession({ persona, onLogout })

    return (
        <div className="relative flex h-full flex-col">
            <ChatHeader
                theme={theme}
                onChangeTheme={onChangeTheme}
                persona={persona}
                onChangePersona={onChangePersona}
                fontSize={fontSize}
                onChangeFontSize={onChangeFontSize}
                blurAi={blurAi}
                onToggleBlurAi={onToggleBlurAi}
                onBack={onBack}
            />
            <MessageList
                messages={s.messages}
                liveTranscript={s.liveTranscript}
                isReplying={s.isReplying}
                fontSize={fontSize}
                blurAi={blurAi}
                hasMore={s.hasMore}
                loadingMore={s.loadingMore}
                onLoadMore={s.loadMore}
            />
            <HeaderBlurMask />
            {s.error && (
                <div className="mx-4 mb-2 rounded-lg bg-destructive/12 px-3 py-2 text-center text-sm text-destructive">
                    出错了:{s.error}
                </div>
            )}
            {s.mode === 'text' ? (
                <TextComposer
                    input={s.input}
                    setInput={s.setInput}
                    status={s.status}
                    isReplying={s.isReplying}
                    statusText={s.statusText}
                    onSend={s.sendText}
                    onInterrupt={s.interrupt}
                    onEnterVoice={s.enterVoice}
                />
            ) : (
                <VoiceControls
                    muted={s.muted}
                    status={s.status}
                    statusText={s.statusText}
                    isReplying={s.isReplying}
                    capturing={s.capturing}
                    onInterrupt={s.interrupt}
                    onToggleMute={s.toggleMute}
                    onEnterText={s.enterText}
                    onHoldStart={s.manualStart}
                    onHoldStop={s.manualStop}
                    onHoldCancel={s.manualCancel}
                />
            )}
        </div>
    )
}
